import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  jsonb,
  varchar,
  date,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { carriers } from "./business";
import { CHILD_RELATIONSHIP_VALUES } from "../constants/dependentRelationships";
import { plans, clientPlans } from "./plans";

export const carrierForms = pgTable("carrier_forms", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  carrierId: integer("carrier_id").references(() => carriers.id),
  planId: integer("plan_id").references(() => plans.id),
  formType: varchar("form_type", { length: 50 }).notNull(),
  coverageLines: text("coverage_lines").array(),
  states: text("states").array(),
  effectiveDate: date("effective_date"),
  expirationDate: date("expiration_date"),
  version: text("version"),
  language: varchar("language", { length: 30 }).default("English"),
  description: text("description"),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  pageCount: integer("page_count").default(0),
  status: varchar("status", { length: 30 }).default("uploaded").notNull(),
  analysisDiagnostics: jsonb("analysis_diagnostics"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const carrierFormFields = pgTable("carrier_form_fields", {
  id: serial("id").primaryKey(),
  formId: integer("form_id").notNull().references(() => carrierForms.id, { onDelete: "cascade" }),
  fieldKey: text("field_key").notNull(),
  fieldLabel: text("field_label").notNull(),
  fieldType: varchar("field_type", { length: 30 }).notNull(),
  pageNumber: integer("page_number").default(1),
  confidence: integer("confidence").default(0),
  isRequired: boolean("is_required").default(false),
  options: text("options").array(),
  description: text("description"),
  fieldCoordinates: jsonb("field_coordinates"),
  mappingStatus: varchar("mapping_status", { length: 20 }).default("needs_review").notNull(),
  reviewedBy: text("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
});

export const carrierFormFieldMappings = pgTable("carrier_form_field_mappings", {
  id: serial("id").primaryKey(),
  formId: integer("form_id").notNull().references(() => carrierForms.id, { onDelete: "cascade" }),
  fieldId: integer("field_id").notNull().references(() => carrierFormFields.id, { onDelete: "cascade" }),
  systemDataSource: varchar("system_data_source", { length: 50 }).notNull(),
  systemFieldPath: varchar("system_field_path", { length: 100 }).notNull(),
  transformRule: text("transform_rule"),
  defaultValue: text("default_value"),
  matchValue: text("match_value"),
  // Specific system/library plan this mapping binds to (FK-less, like plans
  // self-refs). Preferred over matchValue at enrollment; matchValue (planCode)
  // remains the fallback so legacy mappings keep working.
  matchPlanId: integer("match_plan_id"),
});

export const planFormAttachments = pgTable("plan_form_attachments", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
  formId: integer("form_id").notNull().references(() => carrierForms.id, { onDelete: "cascade" }),
  isRequired: boolean("is_required").default(true),
  autoAttach: boolean("auto_attach").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const clientPlanForms = pgTable("client_plan_forms", {
  id: serial("id").primaryKey(),
  clientPlanId: integer("client_plan_id").notNull().references(() => clientPlans.id, { onDelete: "cascade" }),
  formId: integer("form_id").notNull().references(() => carrierForms.id, { onDelete: "cascade" }),
  planFormAttachmentId: integer("plan_form_attachment_id").references(() => planFormAttachments.id),
  status: varchar("status", { length: 30 }).default("pending").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export type PlanFormAttachment = typeof planFormAttachments.$inferSelect;
export type ClientPlanForm = typeof clientPlanForms.$inferSelect;

export const FORM_TYPES = [
  "enrollment",
  "change",
  "termination",
  "waiver",
  "evidence_of_insurability",
  "beneficiary_designation",
  "cobra",
  "salary_reduction_agreement",
  "section_125",
  "qualifying_life_event",
  "continuation",
  "application",
  "group_application",
  "employee_census",
  "other",
] as const;

export const COVERAGE_LINES = [
  { value: "medical", label: "Medical", category: "Health" },
  { value: "dental", label: "Dental", category: "Health" },
  { value: "vision", label: "Vision", category: "Health" },
  { value: "life", label: "Life", category: "Life & Disability" },
  { value: "add", label: "AD&D", category: "Life & Disability" },
  { value: "std", label: "Short-Term Disability", category: "Life & Disability" },
  { value: "ltd", label: "Long-Term Disability", category: "Life & Disability" },
  { value: "critical_illness", label: "Critical Illness", category: "Voluntary" },
  { value: "accident", label: "Accident", category: "Voluntary" },
  { value: "hospital_indemnity", label: "Hospital Indemnity", category: "Voluntary" },
  { value: "cancer", label: "Cancer", category: "Voluntary" },
  { value: "fsa", label: "FSA", category: "Tax-Advantaged" },
  { value: "dcfsa", label: "Dependent Care FSA", category: "Tax-Advantaged" },
  { value: "hsa", label: "HSA", category: "Tax-Advantaged" },
  { value: "hra", label: "HRA", category: "Tax-Advantaged" },
  { value: "eap", label: "EAP", category: "Ancillary" },
  { value: "legal", label: "Legal", category: "Ancillary" },
  { value: "identity_theft", label: "Identity Theft", category: "Ancillary" },
  { value: "pet", label: "Pet Insurance", category: "Ancillary" },
  { value: "commuter", label: "Commuter Benefits", category: "Ancillary" },
  { value: "retirement_401k", label: "401(k)", category: "Retirement" },
] as const;

export const COVERAGE_CATEGORIES = ["Health", "Life & Disability", "Voluntary", "Tax-Advantaged", "Ancillary", "Retirement"] as const;

export const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "DC", label: "Washington DC" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
] as const;

export const FORM_LANGUAGES = ["English", "Spanish", "Chinese", "Vietnamese", "Korean", "Tagalog"] as const;

export const SYSTEM_DATA_SOURCES = {
  employee: {
    label: "Employee",
    dbTable: "employees",
    fields: [
      { path: "employee.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "employee.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "employee.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "employee.middleName", dbColumn: "middle_name", label: "Middle Name", type: "text" },
      { path: "employee.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "employee.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "employee.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "employee.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "employee.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "employee.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "employee.email", dbColumn: "email", label: "Work Email", type: "email" },
      { path: "employee.personalEmail", dbColumn: "personal_email", label: "Personal Email", type: "email" },
      { path: "employee.phone", dbColumn: "phone", label: "Personal Phone", type: "phone" },
      { path: "employee.workPhone", dbColumn: "work_phone", label: "Work Phone", type: "phone" },
      { path: "employee.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "employee.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "employee.city", dbColumn: "city", label: "City", type: "text" },
      { path: "employee.state", dbColumn: "state", label: "State", type: "text" },
      { path: "employee.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "employee.county", dbColumn: "county", label: "County", type: "text" },
      { path: "employee.mailingAddress1", dbColumn: "mailing_address1", label: "Mailing Address", type: "text" },
      { path: "employee.mailingAddress2", dbColumn: "mailing_address2", label: "Mailing Apt/Suite #", type: "text" },
      { path: "employee.mailingCity", dbColumn: "mailing_city", label: "Mailing City", type: "text" },
      { path: "employee.mailingState", dbColumn: "mailing_state", label: "Mailing State", type: "text" },
      { path: "employee.mailingZipCode", dbColumn: "mailing_zip_code", label: "Mailing Zip", type: "text" },
      { path: "employee.mailingCounty", dbColumn: "mailing_county", label: "Mailing County", type: "text" },
      { path: "employee.preferredLanguage", dbColumn: "preferred_language", label: "Preferred Language", type: "text" },
      { path: "employee.hireDate", dbColumn: "hire_date", label: "Hire Date", type: "date" },
      { path: "employee.fullTimeDate", dbColumn: "full_time_date", label: "Full-Time Employment Date", type: "date" },
      { path: "employee.position", dbColumn: "position", label: "Job Title / Position", type: "text" },
      { path: "employee.department", dbColumn: "department", label: "Department", type: "text" },
      { path: "employee.salary", dbColumn: "salary", label: "Salary", type: "currency" },
      { path: "employee.compensationAmount", dbColumn: "compensation_amount", label: "Compensation Amount", type: "currency" },
      { path: "employee.scheduledHours", dbColumn: "scheduled_hours", label: "Hours Per Week", type: "number" },
      { path: "employee.employmentType", dbColumn: "employment_type", label: "Employment Type", type: "text" },
      { path: "employee.maritalStatus", dbColumn: "marital_status", label: "Marital Status", type: "text" },
      { path: "employee.maritalMarried", dbColumn: "marital_status", label: "Married (checkbox)", type: "checkbox", derivedValue: "Married" },
      { path: "employee.maritalSingle", dbColumn: "marital_status", label: "Single (checkbox)", type: "checkbox", derivedValue: "Single" },
      { path: "employee.maritalDomesticPartner", dbColumn: "marital_status", label: "Domestic Partner (checkbox)", type: "checkbox", derivedValue: "Domestic Partner" },
      { path: "employee.tobaccoUser", dbColumn: "tobacco_user", label: "Tobacco User", type: "text" },
      { path: "employee.disabled", dbColumn: "disabled", label: "Disabled", type: "text" },
      { path: "employee.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "employee.employeeId", dbColumn: "employee_id", label: "Employee ID (Employer #)", type: "text" },
      { path: "employee.compensationType", dbColumn: "compensation_type", label: "Compensation Type (Salary/Hourly)", type: "text" },
      { path: "employee.payCycle", dbColumn: "pay_cycle", label: "Pay Cycle", type: "text" },
      { path: "employee.emergencyContact", dbColumn: "emergency_contact", label: "Emergency Contact Name", type: "text" },
      { path: "employee.emergencyPhone", dbColumn: "emergency_phone", label: "Emergency Contact Phone", type: "phone" },
      { path: "employee.terminationDate", dbColumn: "termination_date", label: "Termination Date", type: "date" },
      { path: "employee.terminationReason", dbColumn: "termination_reason", label: "Termination Reason", type: "text" },
    ],
  },
  employer: {
    label: "Employer",
    dbTable: "clients",
    fields: [
      { path: "employer.name", dbColumn: "name", label: "Company Name", type: "text" },
      { path: "employer.ein", dbColumn: "ein", label: "EIN / Tax ID", type: "text" },
      { path: "employer.groupNumber", dbColumn: "group_number", label: "Group Number", type: "text" },
      { path: "employer.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "employer.address2", dbColumn: "address2", label: "Suite/Floor", type: "text" },
      { path: "employer.city", dbColumn: "city", label: "City", type: "text" },
      { path: "employer.state", dbColumn: "state", label: "State", type: "text" },
      { path: "employer.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "employer.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "employer.contactPhone", dbColumn: "contact_phone", label: "Phone", type: "phone" },
      { path: "employer.primaryContactName", dbColumn: "primary_contact_name", label: "Contact Name", type: "text" },
      { path: "employer.contactEmail", dbColumn: "contact_email", label: "Contact Email", type: "email" },
      { path: "employer.sic", dbColumn: "sic", label: "SIC Code", type: "text" },
      { path: "employer.entityType", dbColumn: "entity_type", label: "Entity Type (Corp/LLC/etc)", type: "text" },
      { path: "employer.aleStatus", dbColumn: "ale_status", label: "ALE Status (ACA)", type: "checkbox" },
      { path: "employer.cobraStatus", dbColumn: "cobra_status", label: "COBRA Subject", type: "checkbox" },
    ],
  },
  spouse: {
    label: "Spouse/Domestic Partner",
    dbTable: "dependents",
    dbFilter: { relationship: ["spouse", "domestic_partner"] },
    fields: [
      { path: "spouse.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "spouse.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "spouse.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "spouse.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "spouse.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "spouse.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "spouse.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "spouse.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "spouse.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "spouse.relationship", dbColumn: "relationship", label: "Relationship (Spouse/DP)", type: "text" },
      { path: "spouse.isSpouse", dbColumn: "relationship", label: "Is Spouse (checkbox)", type: "checkbox", derivedValue: "spouse" },
      { path: "spouse.isDomesticPartner", dbColumn: "relationship", label: "Is Domestic Partner (checkbox)", type: "checkbox", derivedValue: "domestic_partner" },
      { path: "spouse.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "spouse.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "spouse.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "spouse.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "spouse.city", dbColumn: "city", label: "City", type: "text" },
      { path: "spouse.state", dbColumn: "state", label: "State", type: "text" },
      { path: "spouse.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "spouse.county", dbColumn: "county", label: "County", type: "text" },
      { path: "spouse.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "spouse.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "spouse.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  dependent1: {
    label: "Dependent Child 1",
    dbTable: "dependents",
    dbFilter: { relationship: [...CHILD_RELATIONSHIP_VALUES], index: 0 },
    fields: [
      { path: "dependent1.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "dependent1.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "dependent1.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "dependent1.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "dependent1.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "dependent1.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "dependent1.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "dependent1.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "dependent1.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "dependent1.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "dependent1.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "dependent1.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "dependent1.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "dependent1.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "dependent1.city", dbColumn: "city", label: "City", type: "text" },
      { path: "dependent1.state", dbColumn: "state", label: "State", type: "text" },
      { path: "dependent1.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "dependent1.county", dbColumn: "county", label: "County", type: "text" },
      { path: "dependent1.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "dependent1.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "dependent1.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  dependent2: {
    label: "Dependent Child 2",
    dbTable: "dependents",
    dbFilter: { relationship: [...CHILD_RELATIONSHIP_VALUES], index: 1 },
    fields: [
      { path: "dependent2.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "dependent2.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "dependent2.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "dependent2.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "dependent2.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "dependent2.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "dependent2.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "dependent2.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "dependent2.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "dependent2.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "dependent2.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "dependent2.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "dependent2.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "dependent2.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "dependent2.city", dbColumn: "city", label: "City", type: "text" },
      { path: "dependent2.state", dbColumn: "state", label: "State", type: "text" },
      { path: "dependent2.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "dependent2.county", dbColumn: "county", label: "County", type: "text" },
      { path: "dependent2.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "dependent2.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "dependent2.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  dependent3: {
    label: "Dependent Child 3",
    dbTable: "dependents",
    dbFilter: { relationship: [...CHILD_RELATIONSHIP_VALUES], index: 2 },
    fields: [
      { path: "dependent3.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "dependent3.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "dependent3.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "dependent3.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "dependent3.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "dependent3.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "dependent3.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "dependent3.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "dependent3.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "dependent3.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "dependent3.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "dependent3.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "dependent3.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "dependent3.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "dependent3.city", dbColumn: "city", label: "City", type: "text" },
      { path: "dependent3.state", dbColumn: "state", label: "State", type: "text" },
      { path: "dependent3.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "dependent3.county", dbColumn: "county", label: "County", type: "text" },
      { path: "dependent3.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "dependent3.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "dependent3.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  dependent4: {
    label: "Dependent Child 4",
    dbTable: "dependents",
    dbFilter: { relationship: [...CHILD_RELATIONSHIP_VALUES], index: 3 },
    fields: [
      { path: "dependent4.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "dependent4.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "dependent4.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "dependent4.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "dependent4.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "dependent4.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "dependent4.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "dependent4.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "dependent4.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "dependent4.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "dependent4.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "dependent4.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "dependent4.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "dependent4.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "dependent4.city", dbColumn: "city", label: "City", type: "text" },
      { path: "dependent4.state", dbColumn: "state", label: "State", type: "text" },
      { path: "dependent4.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "dependent4.county", dbColumn: "county", label: "County", type: "text" },
      { path: "dependent4.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "dependent4.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "dependent4.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  dependent5: {
    label: "Dependent Child 5",
    dbTable: "dependents",
    dbFilter: { relationship: [...CHILD_RELATIONSHIP_VALUES], index: 4 },
    fields: [
      { path: "dependent5.firstName", dbColumn: "first_name", label: "First Name", type: "text" },
      { path: "dependent5.lastName", dbColumn: "last_name", label: "Last Name", type: "text" },
      { path: "dependent5.middleInitial", dbColumn: "middle_initial", label: "Middle Initial", type: "text" },
      { path: "dependent5.formerName", dbColumn: "former_name", label: "Former/Maiden Name", type: "text" },
      { path: "dependent5.ssn", dbColumn: "ssn", label: "SSN", type: "ssn" },
      { path: "dependent5.dateOfBirth", dbColumn: "date_of_birth", label: "Date of Birth", type: "date" },
      { path: "dependent5.sex", dbColumn: "sex", label: "Sex/Gender", type: "text" },
      { path: "dependent5.sexMale", dbColumn: "sex", label: "Male (checkbox)", type: "checkbox", derivedValue: "Male" },
      { path: "dependent5.sexFemale", dbColumn: "sex", label: "Female (checkbox)", type: "checkbox", derivedValue: "Female" },
      { path: "dependent5.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "dependent5.disabled", dbColumn: "disabled", label: "Disabled", type: "checkbox" },
      { path: "dependent5.hasDifferentAddress", dbColumn: "has_different_address", label: "Lives at Different Address (checkbox)", type: "checkbox" },
      { path: "dependent5.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "dependent5.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "dependent5.city", dbColumn: "city", label: "City", type: "text" },
      { path: "dependent5.state", dbColumn: "state", label: "State", type: "text" },
      { path: "dependent5.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "dependent5.county", dbColumn: "county", label: "County", type: "text" },
      { path: "dependent5.country", dbColumn: "country", label: "Country", type: "text" },
      { path: "dependent5.ssnNotAvailable", dbColumn: "ssn_not_available", label: "SSN Not Available (checkbox)", type: "checkbox" },
      { path: "dependent5.ssnFollowUpDate", dbColumn: "ssn_follow_up_date", label: "SSN Follow-Up Date", type: "date" },
    ],
  },
  medicalPlan: {
    label: "Medical Plan",
    dbTable: "plans",
    dbFilter: { type: ["health", "medical"] },
    fields: [
      { path: "medicalPlan.planName", dbColumn: "name", label: "Plan Name", type: "text" },
      { path: "medicalPlan.planCheckbox", label: "Plan Selection (checkbox)", type: "checkbox" },
      { path: "medicalPlan.planCode", dbColumn: "plan_code", label: "Plan Code", type: "text" },
      { path: "medicalPlan.adminName", dbColumn: "admin_name", label: "Admin Plan Name", type: "text" },
      { path: "medicalPlan.networkType", dbColumn: "network_type", label: "Network Type", type: "text" },
      { path: "medicalPlan.metalTier", dbColumn: "metal_tier", label: "Metal Tier (ACA)", type: "text" },
      { path: "medicalPlan.description", dbColumn: "description", label: "Description", type: "text" },
      { path: "medicalPlan.taxDetermination", dbColumn: "tax_determination", label: "Pre-Tax / Post-Tax", type: "text" },
      { path: "medicalPlan.waitingPeriod", dbColumn: "waiting_period", label: "Waiting Period", type: "number" },
      { path: "medicalPlan.subGroupNumber", dbColumn: "sub_group_number", label: "Sub-Group Number", type: "text" },
      { path: "medicalPlan.networkHmo", label: "HMO (checkbox)", type: "checkbox" },
      { path: "medicalPlan.networkPpo", label: "PPO (checkbox)", type: "checkbox" },
      { path: "medicalPlan.networkHsa", label: "HSA (checkbox)", type: "checkbox" },
      { path: "medicalPlan.networkEpo", label: "EPO (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageEmployee", label: "Coverage - Employee (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageSpouse", label: "Coverage - Spouse (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageChild", label: "Coverage - Child(ren) (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDependent1", label: "Coverage - Dependent 1 (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDependent2", label: "Coverage - Dependent 2 (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDependent3", label: "Coverage - Dependent 3 (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDependent4", label: "Coverage - Dependent 4 (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDependent5", label: "Coverage - Dependent 5 (checkbox)", type: "checkbox" },
      { path: "medicalPlan.coverageDecline", label: "Coverage - Decline (checkbox)", type: "checkbox" },
    ],
  },
  dentalPlan: {
    label: "Dental Plan",
    dbTable: "plans",
    dbFilter: { type: ["dental"] },
    fields: [
      { path: "dentalPlan.planName", dbColumn: "name", label: "Plan Name", type: "text" },
      { path: "dentalPlan.planCheckbox", label: "Plan Selection (checkbox)", type: "checkbox" },
      { path: "dentalPlan.planCode", dbColumn: "plan_code", label: "Plan Code", type: "text" },
      { path: "dentalPlan.adminName", dbColumn: "admin_name", label: "Admin Plan Name", type: "text" },
      { path: "dentalPlan.networkType", dbColumn: "network_type", label: "Network Type", type: "text" },
      { path: "dentalPlan.description", dbColumn: "description", label: "Description", type: "text" },
      { path: "dentalPlan.taxDetermination", dbColumn: "tax_determination", label: "Pre-Tax / Post-Tax", type: "text" },
      { path: "dentalPlan.subGroupNumber", dbColumn: "sub_group_number", label: "Sub-Group Number", type: "text" },
      { path: "dentalPlan.coverageEmployee", label: "Coverage - Employee (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageSpouse", label: "Coverage - Spouse (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageChild", label: "Coverage - Child(ren) (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDependent1", label: "Coverage - Dependent 1 (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDependent2", label: "Coverage - Dependent 2 (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDependent3", label: "Coverage - Dependent 3 (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDependent4", label: "Coverage - Dependent 4 (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDependent5", label: "Coverage - Dependent 5 (checkbox)", type: "checkbox" },
      { path: "dentalPlan.coverageDecline", label: "Coverage - Decline (checkbox)", type: "checkbox" },
    ],
  },
  visionPlan: {
    label: "Vision Plan",
    dbTable: "plans",
    dbFilter: { type: ["vision"] },
    fields: [
      { path: "visionPlan.planName", dbColumn: "name", label: "Plan Name", type: "text" },
      { path: "visionPlan.planCheckbox", label: "Plan Selection (checkbox)", type: "checkbox" },
      { path: "visionPlan.planCode", dbColumn: "plan_code", label: "Plan Code", type: "text" },
      { path: "visionPlan.adminName", dbColumn: "admin_name", label: "Admin Plan Name", type: "text" },
      { path: "visionPlan.networkType", dbColumn: "network_type", label: "Network Type", type: "text" },
      { path: "visionPlan.description", dbColumn: "description", label: "Description", type: "text" },
      { path: "visionPlan.taxDetermination", dbColumn: "tax_determination", label: "Pre-Tax / Post-Tax", type: "text" },
      { path: "visionPlan.subGroupNumber", dbColumn: "sub_group_number", label: "Sub-Group Number", type: "text" },
      { path: "visionPlan.coverageEmployee", label: "Coverage - Employee (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageSpouse", label: "Coverage - Spouse (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageChild", label: "Coverage - Child(ren) (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDependent1", label: "Coverage - Dependent 1 (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDependent2", label: "Coverage - Dependent 2 (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDependent3", label: "Coverage - Dependent 3 (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDependent4", label: "Coverage - Dependent 4 (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDependent5", label: "Coverage - Dependent 5 (checkbox)", type: "checkbox" },
      { path: "visionPlan.coverageDecline", label: "Coverage - Decline (checkbox)", type: "checkbox" },
    ],
  },
  lifePlan: {
    label: "Life Plan",
    dbTable: "plans",
    dbFilter: { type: ["life"] },
    fields: [
      { path: "lifePlan.planName", dbColumn: "name", label: "Plan Name", type: "text" },
      { path: "lifePlan.planCheckbox", label: "Plan Selection (checkbox)", type: "checkbox" },
      { path: "lifePlan.planCode", dbColumn: "plan_code", label: "Plan Code", type: "text" },
      { path: "lifePlan.adminName", dbColumn: "admin_name", label: "Admin Plan Name", type: "text" },
      { path: "lifePlan.description", dbColumn: "description", label: "Description", type: "text" },
      { path: "lifePlan.taxDetermination", dbColumn: "tax_determination", label: "Pre-Tax / Post-Tax", type: "text" },
      { path: "lifePlan.subGroupNumber", dbColumn: "sub_group_number", label: "Sub-Group Number", type: "text" },
      { path: "lifePlan.coverageEmployee", label: "Coverage - Employee (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageSpouse", label: "Coverage - Spouse (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageChild", label: "Coverage - Child(ren) (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDependent1", label: "Coverage - Dependent 1 (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDependent2", label: "Coverage - Dependent 2 (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDependent3", label: "Coverage - Dependent 3 (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDependent4", label: "Coverage - Dependent 4 (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDependent5", label: "Coverage - Dependent 5 (checkbox)", type: "checkbox" },
      { path: "lifePlan.coverageDecline", label: "Coverage - Decline (checkbox)", type: "checkbox" },
    ],
  },
  enrollment: {
    label: "Enrollment",
    dbTable: "enrollments",
    fields: [
      { path: "enrollment.effectiveDate", dbColumn: "effective_date", label: "Effective Date", type: "date" },
      { path: "enrollment.endDate", dbColumn: "end_date", label: "Termination / End Date", type: "date" },
      { path: "enrollment.coverageTier", dbColumn: "coverage_tier", label: "Coverage Tier", type: "text" },
      { path: "enrollment.enrollmentSource", dbColumn: "enrollment_source", label: "Enrollment Source/Type", type: "text" },
      { path: "enrollment.isNewHire", dbColumn: "enrollment_source", label: "New Hire (checkbox)", type: "checkbox", derivedValue: "new_hire" },
      { path: "enrollment.isOpenEnrollment", dbColumn: "enrollment_source", label: "Open Enrollment (checkbox)", type: "checkbox", derivedValue: "open_enrollment" },
      { path: "enrollment.isQualifyingEvent", dbColumn: "enrollment_source", label: "Qualifying Event (checkbox)", type: "checkbox", derivedValue: "qualifying_event" },
      { path: "enrollment.isCobra", dbColumn: "cobra_eligible", label: "COBRA (checkbox)", type: "checkbox" },
      { path: "enrollment.eventType", dbColumn: "event_type", label: "Event Type", type: "text" },
      { path: "enrollment.eventDescription", dbColumn: "event_description", label: "Event Description", type: "text" },
      { path: "enrollment.waivedCoverage", dbColumn: "waived_coverage", label: "Waived Coverage (checkbox)", type: "checkbox" },
      { path: "enrollment.waiverReason", dbColumn: "waiver_reason", label: "Waiver Reason", type: "text" },
      { path: "enrollment.hsaEligible", dbColumn: "hsa_eligible", label: "HSA Eligible (checkbox)", type: "checkbox" },
      { path: "enrollment.premiumOnlyPlan", label: "Premium Only Plan (checkbox)", type: "checkbox" },
      { path: "enrollment.enrollmentDate", dbColumn: "enrollment_date", label: "Enrollment Submitted Date", type: "date" },
      { path: "enrollment.coverageAmount", dbColumn: "coverage_amount", label: "Elected Coverage Amount (Life/Disability)", type: "currency" },
      { path: "enrollment.coverageVolume", dbColumn: "coverage_volume", label: "Coverage Volume (per $1,000)", type: "currency" },
      { path: "enrollment.annualElection", dbColumn: "annual_election", label: "Annual Election (FSA/HSA)", type: "currency" },
      { path: "enrollment.employeePremium", dbColumn: "employee_premium", label: "Employee Premium", type: "currency" },
      { path: "enrollment.employerPremium", dbColumn: "employer_premium", label: "Employer Premium", type: "currency" },
      { path: "enrollment.totalPremium", dbColumn: "total_premium", label: "Total Premium", type: "currency" },
      { path: "enrollment.payrollDeduction", dbColumn: "payroll_deduction", label: "Payroll Deduction", type: "currency" },
      { path: "enrollment.beneficiaryName", dbColumn: "beneficiary_name", label: "Beneficiary Name (simple)", type: "text" },
      { path: "enrollment.beneficiaryRelationship", dbColumn: "beneficiary_relationship", label: "Beneficiary Relationship (simple)", type: "text" },
    ],
  },
  emergencyContact: {
    label: "Emergency Contact",
    dbTable: "emergency_contacts",
    fields: [
      { path: "emergencyContact.name", dbColumn: "name", label: "Name", type: "text" },
      { path: "emergencyContact.relationship", dbColumn: "relationship", label: "Relationship", type: "text" },
      { path: "emergencyContact.phone", dbColumn: "phone", label: "Phone", type: "phone" },
      { path: "emergencyContact.email", dbColumn: "email", label: "Email", type: "email" },
      { path: "emergencyContact.address1", dbColumn: "address1", label: "Street Address", type: "text" },
      { path: "emergencyContact.address2", dbColumn: "address2", label: "Apt/Suite #", type: "text" },
      { path: "emergencyContact.city", dbColumn: "city", label: "City", type: "text" },
      { path: "emergencyContact.state", dbColumn: "state", label: "State", type: "text" },
      { path: "emergencyContact.zipCode", dbColumn: "zip_code", label: "Zip Code", type: "text" },
      { path: "emergencyContact.county", dbColumn: "county", label: "County", type: "text" },
    ],
  },
  beneficiary: {
    label: "Beneficiary",
    dbTable: "life_insurance_beneficiaries",
    fields: [
      { path: "beneficiary1.firstName", dbColumn: "first_name", label: "Beneficiary 1 First Name", type: "text" },
      { path: "beneficiary1.lastName", dbColumn: "last_name", label: "Beneficiary 1 Last Name", type: "text" },
      { path: "beneficiary1.middleName", dbColumn: "middle_name", label: "Beneficiary 1 Middle Name", type: "text" },
      { path: "beneficiary1.dateOfBirth", dbColumn: "date_of_birth", label: "Beneficiary 1 DOB", type: "date" },
      { path: "beneficiary1.relationship", dbColumn: "relationship", label: "Beneficiary 1 Relationship", type: "text" },
      { path: "beneficiary1.percentage", dbColumn: "percentage", label: "Beneficiary 1 Percentage", type: "number" },
      { path: "beneficiary1.ssn", dbColumn: "ssn", label: "Beneficiary 1 SSN", type: "ssn" },
      { path: "beneficiary1.address", dbColumn: "address_line1", label: "Beneficiary 1 Address", type: "text" },
      { path: "beneficiary1.beneficiaryType", dbColumn: "beneficiary_type", label: "Beneficiary 1 Type (Primary/Contingent)", type: "text" },
      { path: "beneficiary1.isPrimary", dbColumn: "beneficiary_type", label: "Primary (checkbox)", type: "checkbox", derivedValue: "primary" },
      { path: "beneficiary1.isContingent", dbColumn: "beneficiary_type", label: "Contingent (checkbox)", type: "checkbox", derivedValue: "contingent" },
      { path: "beneficiary2.firstName", dbColumn: "first_name", label: "Beneficiary 2 First Name", type: "text" },
      { path: "beneficiary2.lastName", dbColumn: "last_name", label: "Beneficiary 2 Last Name", type: "text" },
      { path: "beneficiary2.middleName", dbColumn: "middle_name", label: "Beneficiary 2 Middle Name", type: "text" },
      { path: "beneficiary2.dateOfBirth", dbColumn: "date_of_birth", label: "Beneficiary 2 DOB", type: "date" },
      { path: "beneficiary2.relationship", dbColumn: "relationship", label: "Beneficiary 2 Relationship", type: "text" },
      { path: "beneficiary2.percentage", dbColumn: "percentage", label: "Beneficiary 2 Percentage", type: "number" },
      { path: "beneficiary2.ssn", dbColumn: "ssn", label: "Beneficiary 2 SSN", type: "ssn" },
      { path: "beneficiary2.address", dbColumn: "address_line1", label: "Beneficiary 2 Address", type: "text" },
      { path: "beneficiary2.beneficiaryType", dbColumn: "beneficiary_type", label: "Beneficiary 2 Type", type: "text" },
      { path: "beneficiary2.isPrimary", dbColumn: "beneficiary_type", label: "Primary (checkbox)", type: "checkbox", derivedValue: "primary" },
      { path: "beneficiary2.isContingent", dbColumn: "beneficiary_type", label: "Contingent (checkbox)", type: "checkbox", derivedValue: "contingent" },
    ],
  },
  pcp: {
    label: "Primary Care Physician",
    dbNote: "PCP data is form-only; not yet stored in a dedicated DB table",
    fields: [
      { path: "pcp.employee.name", label: "Employee PCP Name", type: "text" },
      { path: "pcp.employee.id", label: "Employee PCP Provider ID", type: "text" },
      { path: "pcp.employee.medicalGroup", label: "Employee PCP Medical Group", type: "text" },
      { path: "pcp.employee.city", label: "Employee PCP City", type: "text" },
      { path: "pcp.employee.currentPatient", label: "Employee Current Patient (checkbox)", type: "checkbox" },
      { path: "pcp.spouse.name", label: "Spouse/DP PCP Name", type: "text" },
      { path: "pcp.spouse.id", label: "Spouse/DP PCP Provider ID", type: "text" },
      { path: "pcp.spouse.medicalGroup", label: "Spouse/DP PCP Medical Group", type: "text" },
      { path: "pcp.spouse.city", label: "Spouse/DP PCP City", type: "text" },
      { path: "pcp.spouse.currentPatient", label: "Spouse/DP Current Patient (checkbox)", type: "checkbox" },
      { path: "pcp.child1.name", label: "Child 1 PCP Name", type: "text" },
      { path: "pcp.child1.id", label: "Child 1 PCP Provider ID", type: "text" },
      { path: "pcp.child1.medicalGroup", label: "Child 1 PCP Medical Group", type: "text" },
      { path: "pcp.child1.city", label: "Child 1 PCP City", type: "text" },
      { path: "pcp.child1.currentPatient", label: "Child 1 Current Patient (checkbox)", type: "checkbox" },
      { path: "pcp.child2.name", label: "Child 2 PCP Name", type: "text" },
      { path: "pcp.child2.id", label: "Child 2 PCP Provider ID", type: "text" },
      { path: "pcp.child2.medicalGroup", label: "Child 2 PCP Medical Group", type: "text" },
      { path: "pcp.child2.city", label: "Child 2 PCP City", type: "text" },
      { path: "pcp.child2.currentPatient", label: "Child 2 Current Patient (checkbox)", type: "checkbox" },
      { path: "pcp.child3.name", label: "Child 3 PCP Name", type: "text" },
      { path: "pcp.child3.id", label: "Child 3 PCP Provider ID", type: "text" },
      { path: "pcp.child3.medicalGroup", label: "Child 3 PCP Medical Group", type: "text" },
      { path: "pcp.child3.city", label: "Child 3 PCP City", type: "text" },
      { path: "pcp.child3.currentPatient", label: "Child 3 Current Patient (checkbox)", type: "checkbox" },
    ],
  },
  waiver: {
    label: "Waiver",
    dbTable: "enrollments",
    dbNote: "Waiver data maps to enrollments.waived_coverage/waiver_reason fields",
    fields: [
      { path: "waiver.waiveMedical", label: "Waive Medical (checkbox)", type: "checkbox" },
      { path: "waiver.waiveDental", label: "Waive Dental (checkbox)", type: "checkbox" },
      { path: "waiver.waiveVision", label: "Waive Vision (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeOtherGroup", label: "Other Group Coverage (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeMedicare", label: "Medicare (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeMediCal", label: "Medi-Cal (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeIndividual", label: "Individual Policy (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeMilitary", label: "Military/TRICARE (checkbox)", type: "checkbox" },
      { path: "waiver.waiverTypeOther", label: "Other (checkbox)", type: "checkbox" },
      { path: "waiver.reason", dbColumn: "waiver_reason", label: "Waiver Reason", type: "text" },
      { path: "waiver.otherCarrierName", label: "Other Carrier Name", type: "text" },
      { path: "waiver.otherGroupPolicyNumber", label: "Other Group Policy Number", type: "text" },
      { path: "waiver.otherGroupEmployerName", label: "Other Group Employer Name", type: "text" },
    ],
  },
  signatures: {
    label: "Signatures",
    dbNote: "Signature data is captured via the signatures table and linked to enrollments",
    fields: [
      { path: "signatures.employeeSignature", label: "Employee Signature", type: "signature" },
      { path: "signatures.employeeSignatureDate", label: "Employee Signature Date", type: "date" },
      { path: "signatures.employeePrintedName", label: "Employee Printed Name", type: "text" },
      { path: "signatures.waiverSignature", label: "Waiver Signature", type: "signature" },
      { path: "signatures.waiverSignatureDate", label: "Waiver Signature Date", type: "date" },
      { path: "signatures.waiverPrintedName", label: "Waiver Print Name", type: "text" },
    ],
  },
} as const;

export const insertCarrierFormSchema = createInsertSchema(carrierForms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCarrierFormFieldSchema = createInsertSchema(carrierFormFields).omit({
  id: true,
});

export const insertCarrierFormFieldMappingSchema = createInsertSchema(carrierFormFieldMappings).omit({
  id: true,
});

export type CarrierForm = typeof carrierForms.$inferSelect;
export type InsertCarrierForm = z.infer<typeof insertCarrierFormSchema>;
export type CarrierFormField = typeof carrierFormFields.$inferSelect;
export type InsertCarrierFormField = z.infer<typeof insertCarrierFormFieldSchema>;
export type CarrierFormFieldMapping = typeof carrierFormFieldMappings.$inferSelect;
export type InsertCarrierFormFieldMapping = z.infer<typeof insertCarrierFormFieldMappingSchema>;

// ── Legacy broker form library tables (broker-facing, agency-scoped) ──────────

export const forms = pgTable(
  "forms",
  {
    id: serial("id").primaryKey(),
    agencyId: varchar("agency_id").notNull(),
    clientId: integer("client_id"),
    carrierId: integer("carrier_id"), // optional - links form to a specific carrier for auto-recommendations
    name: text("name").notNull(),
    description: text("description"),
    documentId: integer("document_id").notNull(),
    formType: text("form_type").notNull().default("custom"),
    status: text("status").notNull().default("active"),
    version: integer("version").default(1),
    previousVersionId: integer("previous_version_id"),
    fieldCount: integer("field_count").default(0),
    lastFieldsDetectedAt: timestamp("last_fields_detected_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("forms_agency_id_idx").on(table.agencyId),
    clientIdIdx: index("forms_client_id_idx").on(table.clientId),
  })
);

export const formFields = pgTable(
  "form_fields",
  {
    id: serial("id").primaryKey(),
    formId: integer("form_id").notNull().references(() => forms.id, { onDelete: "cascade" }),
    fieldName: text("field_name").notNull(),
    fieldLabel: text("field_label").notNull(),
    fieldType: text("field_type").notNull().default("text"),
    pageNumber: integer("page_number"),
    fieldCoordinates: jsonb("field_coordinates"),
    required: boolean("required").default(false),
    placeholder: text("placeholder"),
    helpText: text("help_text"),
    fieldOptions: text("field_options").array(),
    detectionMethod: text("detection_method").default("manual"),
    confidence: integer("confidence").default(100),
    mappingStatus: varchar("mapping_status", { length: 20 }).default("needs_review").notNull(),
    reviewedBy: text("reviewed_by"),
    reviewedAt: timestamp("reviewed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    formIdIdx: index("form_fields_form_id_idx").on(table.formId),
  })
);

export const formFieldMappings = pgTable(
  "form_field_mappings",
  {
    id: serial("id").primaryKey(),
    formId: integer("form_id").notNull().references(() => forms.id, { onDelete: "cascade" }),
    formFieldId: integer("form_field_id").notNull().references(() => formFields.id, { onDelete: "cascade" }),
    sourceType: text("source_type").notNull(),
    sourcePath: text("source_path").notNull(),
    transformationType: text("transformation_type").default("none"),
    transformationConfig: jsonb("transformation_config"),
    isCalculated: boolean("is_calculated").default(false),
    calculationLogic: text("calculation_logic"),
    conditionalLogic: jsonb("conditional_logic"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    formIdIdx: index("form_field_mappings_form_id_idx").on(table.formId),
    formFieldIdIdx: index("form_field_mappings_form_field_id_idx").on(table.formFieldId),
  })
);

export const planFormAssociations = pgTable(
  "plan_form_associations",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    formId: integer("form_id").references(() => forms.id, { onDelete: "cascade" }),
    carrierFormId: integer("carrier_form_id").references(() => carrierForms.id, { onDelete: "cascade" }),
    enrollmentStep: integer("enrollment_step"),
    displayOrder: integer("display_order").default(0),
    isRequired: boolean("is_required").default(true),
    allowSkip: boolean("allow_skip").default(false),
    skipMessage: text("skip_message"),
    showForCoverageTiers: text("show_for_coverage_tiers").array(),
    status: text("status").notNull().default("active"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    planIdIdx: index("plan_form_associations_plan_id_idx").on(table.planId),
    formIdIdx: index("plan_form_associations_form_id_idx").on(table.formId),
  })
);

export const formSubmissions = pgTable(
  "form_submissions",
  {
    id: serial("id").primaryKey(),
    formId: integer("form_id").notNull().references(() => forms.id, { onDelete: "cascade" }),
    employeeId: integer("employee_id").notNull(),
    enrollmentId: integer("enrollment_id"),
    submissionData: jsonb("submission_data").notNull(),
    status: text("status").notNull().default("submitted"),
    submittedAt: timestamp("submitted_at").notNull().defaultNow(),
    reviewedAt: timestamp("reviewed_at"),
    reviewedByUserId: varchar("reviewed_by_user_id"),
    rejectionReason: text("rejection_reason"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    formIdIdx: index("form_submissions_form_id_idx").on(table.formId),
    employeeIdIdx: index("form_submissions_employee_id_idx").on(table.employeeId),
  })
);

export const insertFormSchema = createInsertSchema(forms).omit({ id: true, createdAt: true, updatedAt: true });
export const insertFormFieldSchema = createInsertSchema(formFields).omit({ id: true, createdAt: true, updatedAt: true });
export const insertFormFieldMappingSchema = createInsertSchema(formFieldMappings).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPlanFormAssociationSchema = createInsertSchema(planFormAssociations).omit({ id: true, createdAt: true, updatedAt: true });
export const insertFormSubmissionSchema = createInsertSchema(formSubmissions).omit({ id: true, createdAt: true, updatedAt: true });

// Legacy broker form library type aliases (used by form library UI components)
export type FormLibraryForm = typeof forms.$inferSelect;
export type InsertFormLibraryForm = z.infer<typeof insertFormSchema>;
export type FormLibraryFormField = typeof formFields.$inferSelect;
export type InsertFormLibraryFormField = z.infer<typeof insertFormFieldSchema>;
export type FormLibraryFormFieldMapping = typeof formFieldMappings.$inferSelect;
export type InsertFormLibraryFormFieldMapping = z.infer<typeof insertFormFieldMappingSchema>;
export type FormLibraryPlanFormAssociation = typeof planFormAssociations.$inferSelect;
export type InsertFormLibraryPlanFormAssociation = z.infer<typeof insertPlanFormAssociationSchema>;
export type FormLibraryFormSubmission = typeof formSubmissions.$inferSelect;
export type InsertFormLibraryFormSubmission = z.infer<typeof insertFormSubmissionSchema>;
